import { Link } from 'react-router-dom';
import { Icon, Tone } from '~/components/ui';
import type { ThreadSummary } from '../api/threadsClient';
import { useLocale, useT } from '~/i18n';
import { absoluteTime, relativeTime } from '../lib/threads';

interface Props {
  thread: ThreadSummary;
}

/**
 * Una fila del inbox: empresa + puesto como título del hilo, el preview del
 * último mensaje y cuándo pasó.
 *
 * El badge de no leídos es CORAL y no mint a propósito: el mint es el dato
 * confirmado y esto es algo que está esperando por el candidato. El mint sí
 * aparece, pero como el sello "Respondieron" —eso sí depende de que la empresa
 * haya contestado— y sólo cuando ya no hay nada sin leer, para que la fila no
 * grite dos cosas a la vez.
 *
 * «Oportunidad» (mint, junto al título) sale cuando el servidor clasificó la
 * respuesta de la empresa como interés, entrevista o pedido de datos
 * (`reply_is_opportunity`). Con ella no hace falta además "Respondieron".
 */
export default function ThreadListItem({ thread }: Props) {
  const t = useT('workspace').threadItem;
  const locale = useLocale();
  const unread = thread.unread_count > 0;
  const replied = thread.last_message_direction === 'inbound';
  const opportunity = thread.reply_is_opportunity === true;

  return (
    <li className="inbox__row">
      <Link
        to={`/mensajes/${thread.application_id}`}
        className="inbox__link"
        data-unread={unread ? 'true' : 'false'}
      >
        <span className="inbox__art" aria-hidden="true">
          <Icon name="briefcase" size={18} />
        </span>

        <span className="inbox__main">
          <span className="inbox__title">
            <strong>{thread.company}</strong>
            <span className="hush">{thread.title}</span>
            {opportunity && <Tone tone="good">{t.opportunity}</Tone>}
          </span>
          {thread.location && <span className="inbox__where">{thread.location}</span>}
          <span className="inbox__preview">
            {thread.last_message_direction === 'outbound' && (
              <span className="text-brand">{t.you}</span>
            )}
            {thread.last_message_preview}
          </span>
        </span>

        <span className="inbox__side">
          <time className="inbox__time" dateTime={thread.last_message_at} title={absoluteTime(thread.last_message_at, locale)}>
            {relativeTime(thread.last_message_at, undefined, locale)}
          </time>
          {unread ? (
            <span className="inbox__unread" aria-label={t.unread(thread.unread_count)}>
              {thread.unread_count}
            </span>
          ) : (
            replied && !opportunity && <Tone tone="good">{t.replied}</Tone>
          )}
        </span>
      </Link>
    </li>
  );
}
